import React from "react"
import { Img } from "@shared/Frontend/components/img"
import { Picture } from "@shared/Frontend/components/picture"
import { InternalLink } from "@shared/Frontend/utils/link"
import { Teaser, TeaserRelated } from "@modules/Nzo.CMS/Frontend/libraries/cmsPage/teasers/teasers.state"
import { Clickable } from "@shared/Frontend/utils/clickable"
import { getSite } from "@shared/Frontend/utils/site"
import { translate } from "@shared/Frontend/utils/translate"
import { SVGIcon } from "@shared/Frontend/utils/svg"
import i18next from "i18next"

const site = getSite()

export const TeaserItem = ({
  title,
  link,
  image,
  pictureSources,
  publicationDate,
  kind,
  showType,
  extraClasses,
  titleExtraClasses,
  intro
}: Teaser) => (
  <li className={`teaser ${extraClasses} ${site == 'ZEG' ? 'teaser--haslink' : image ? 'teaser--haslink' : ''}`}>
    <article className="teaser__article">
      <div className="teaser__wrap">
        <div className="teaser__inner">
          <div className="teaser__content ls-reversed-column">
            <h2 className={`teaser__title ${titleExtraClasses}`}>{title}</h2>
            <div className='teaser__meta meta text--sm'>
              {publicationDate &&
                <time dateTime={publicationDate.toISOString()} className="meta__date">
                  {publicationDate.toLocaleString(
                    i18next.language,
                    { day: 'numeric', month: 'long', year: 'numeric' })
                  }
                </time>
              }
              {showType &&
                <span className="meta__type">{translate(`common:contentTypes:${kind.toLocaleLowerCase()}`, {}, '')}</span>
              }
            </div>
          </div>

          {intro &&
            <div className="teaser__intro text--lg">{intro}</div>
          }

          <footer className="teaser__footer">
              {site == 'ZEG' && (
                <div className="teaser__icon">
                  <SVGIcon id={'icon-chevron-right'} className='teaser__icon--chevron' size={20} />
                  <SVGIcon id={'icon-circle'} className={`teaser__icon--blob-filled`} width={32} height={32} />
                </div>
              )}
              <InternalLink {...link} className='teaser__clickable'>
                  <span className='sr-text'>Lees verder</span>
              </InternalLink>
          </footer>


          {site !== 'ZEG' && (
            <div className="teaser__visual fit-object">
              {image ? (
                pictureSources ? (
                  <Picture
                    pictureSources={pictureSources}
                    {...image}
                    loading="lazy"
                    className="teaser__image"
                    width='176'
                    height='176'
                    sizes="176px"
                  />
                ) : (
                  <Img {...image} loading="lazy" className="teaser__image" width="176" height="176" sizes="176px" />
                )
              ) : <></>}
            </div>
          )}
        </div>
      </div>
    </article>
  </li>
)

export const TeaserRelatedItem = ({
  title,
  link,
  image,
  pictureSources,
  clickableLabel
}: TeaserRelated) => (
  <li className='teaser teaser--showlink'>
    <article className="teaser__article">
      <div className="teaser__wrap">
        <div className="teaser__inner">
          <div className="teaser__content">
            <h2 className="teaser__title title--sm">{title}</h2>
          </div>

          <footer className="teaser__footer">
            <Clickable
              elementKind="link"
              styleKind="link"
              external={false}
              label={clickableLabel}
              {...link}
              className="teaser__clickable link link--hollow"
              hasBlob={true}
              icon={'icon-chevron-right'}
            />
          </footer>

          <div className="teaser__visual fit-object">
            {image ? (
              pictureSources ? (
                <Picture
                  pictureSources={pictureSources}
                  {...image}
                  loading="lazy"
                  className="teaser__image"
                  width='176'
                  height='176'
                  sizes="176px"
                />
              ) : (
                <Img {...image} loading="lazy" className="teaser__image" width="176" height="176" sizes="176px" />
              )
            ) : <></>}
          </div>
        </div>
      </div>
    </article>
  </li>
)

export const TeaserGridItem = ({
  title,
  link,
  image,
  pictureSources,
  publicationDate,
  kind,
  categories,
  showType
}: Teaser) => {
  return (
  <li className="grid__item grid__item--actueel">
    <article className="grid__article">
      {image ? (
        <div className="grid__visual grid__visual--image fit-object" aria-hidden="true">
          {pictureSources ? (
            <Picture
              pictureSources={pictureSources}
              {...image}
              loading="lazy"
              className="grid__image"
              width={'384'}
              height={'256'}
            />
          ) : (
            <Img {...image} loading="lazy" className="grid__image" width="384" height="256" />
          )}
          <svg className="grid__shape" width="400" height="318" viewBox="0 0 400 318" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M400 318H0V0H400V318ZM198.521 24.8076C126.299 7.37734 54.078 51.4113 35.7939 123.885C18.4242 196.358 62.3059 268.833 134.527 287.181C206.467 304.543 346.436 305.521 365.599 233.9L366.036 232.148C384.32 159.675 270.742 43.1554 198.521 24.8076Z" /></svg>
        </div>
      ) : (
        <div className="grid__visual grid__visual--placeholder" aria-hidden="true">
          <svg className="grid__shape" width="400" height="318" viewBox="0 0 400 318" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M400 318H0V0H400V318ZM198.521 24.8076C126.299 7.37734 54.078 51.4113 35.7939 123.885C18.4242 196.358 62.3059 268.833 134.527 287.181C206.467 304.543 346.436 305.521 365.599 233.9L366.036 232.148C384.32 159.675 270.742 43.1554 198.521 24.8076Z" /></svg>
        </div>
      )}

      <div className="grid__text">
        <header className="grid__header">
          <div className="grid__header-inner ls-reversed-column">
            <h2 className="grid__title title--xs">{title}</h2>
            <div className='grid__meta meta text--sm'>
              {publicationDate &&
                <time dateTime={publicationDate.toISOString()} className="meta__date">
                  {publicationDate.toLocaleString(
                    i18next.language,
                    { day: 'numeric', month: 'long', year: 'numeric' })
                  }
                </time>
              }
              {showType &&
                <span className="meta__type">{translate(`common:contentTypes:${kind.toLocaleLowerCase()}`, {}, '')}</span>
              }
            </div>
          </div>

          {categories?.map(category => <div className='grid__tag text--sm'>{category.name}</div>)}
        </header>

        <footer className="grid__footer">
            <InternalLink {...link} className='grid__clickable'>
                <span className='sr-text'>Lees verder</span>
            </InternalLink>
        </footer>
      </div>
    </article>
  </li>
)
}
